import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Check, Copy, Pencil, Trash2, X, Search } from 'lucide-react';
import { activitiesAPI, moderationAPI } from '../services/api';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import ActivityForm from '../components/Forms/ActivityForm';
import { Card } from '../components/Common/Card';
import { Button } from '../components/Common/Button';

// Nur die Felder, die die Tabelle anzeigt. Der Bearbeitungsdialog lädt den
// vollständigen Datensatz separat nach.
const LIST_FIELDS = 'id,name,address,age_min,age_max,website,status,created_at';
const EMPTY_COUNTS = { all: 0, pending: 0, active: 0, rejected: 0, archived: 0 };

const Moderation = () => {
  const { user, loading: authLoading } = useAuth();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'active', 'archived'
  const [searchTerm, setSearchTerm] = useState('');
  const [editingActivity, setEditingActivity] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const editPanelRef = useRef(null);
  const isModerator = user?.role === 'moderator' || user?.role === 'admin';
  const debouncedSearch = useDebouncedValue(searchTerm);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await moderationAPI.getAll({
        status: filter === 'all' ? undefined : filter,
        q: debouncedSearch.trim() || undefined,
        fields: LIST_FIELDS,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      });
      const { items, total: totalCount, counts: statusCounts } = response.data;
      setActivities(items);
      setTotal(totalCount);
      setCounts(statusCounts || EMPTY_COUNTS);
      // Nach dem Löschen des letzten Eintrags einer Seite nicht ins Leere laufen
      if (items.length === 0 && totalCount > 0 && currentPage > 1) {
        setCurrentPage(Math.max(1, Math.ceil(totalCount / itemsPerPage)));
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      alert('Fehler beim Laden der Aktivitäten: ' + error.message);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [filter, debouncedSearch, currentPage]);

  useEffect(() => {
    // Warte bis Auth geladen ist
    if (authLoading) {
      return;
    }

    if (!isModerator) {
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    // Solange die Eingabe noch entprellt wird, nicht mit dem alten Suchbegriff laden
    if (searchTerm.trim() !== debouncedSearch.trim()) {
      return;
    }

    loadActivities();
  }, [isModerator, authLoading, loadActivities, searchTerm, debouncedSearch]);

  // Automatisch zum Edit-Formular scrollen, wenn bearbeitet wird
  useEffect(() => {
    if (editingActivity && editPanelRef.current) {
      editPanelRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
      
      // Fokussiere das erste Eingabefeld nach kurzer Verzögerung
      setTimeout(() => {
        const firstInput = editPanelRef.current?.querySelector('input, textarea');
        if (firstInput) {
          firstInput.focus();
        }
      }, 500);
    }
  }, [editingActivity]);

  const handleApprove = async (activityId) => {
    if (!window.confirm('Möchten Sie diese Aktivität genehmigen?')) return;
    
    try {
      await moderationAPI.approve(activityId);
      loadActivities();
    } catch (error) {
      console.error('Error approving activity:', error);
      alert('Fehler beim Genehmigen der Aktivität');
    }
  };

  const handleReject = async (activityId) => {
    if (!window.confirm('Möchten Sie diese Aktivität ablehnen?')) return;

    const reason = window.prompt('Bitte geben Sie eine Begründung für die Ablehnung ein:');
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Eine Begründung ist erforderlich.');
      return;
    }
    
    try {
      await moderationAPI.reject(activityId, reason.trim());
      loadActivities();
    } catch (error) {
      console.error('Error rejecting activity:', error);
      const apiMessage = error.response?.data?.error;
      alert(apiMessage || 'Fehler beim Ablehnen der Aktivität');
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Möchten Sie diese Aktivität wirklich löschen?')) return;
    
    try {
      await activitiesAPI.delete(activityId);
      loadActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Fehler beim Löschen der Aktivität');
    }
  };

  // Kopie anlegen und direkt zum Bearbeiten öffnen – kopiert wird ja, um
  // anschliessend Datum, Ort oder Name der Kopie anzupassen. Die Antwort des
  // Backends ist bereits der vollständige Datensatz der Kopie.
  const handleCopy = async (activity) => {
    if (!window.confirm(`Möchten Sie eine Kopie von "${activity.name}" anlegen?`)) return;

    try {
      const response = await activitiesAPI.duplicate(activity.id);
      await loadActivities();
      setEditingActivity(response.data);
    } catch (error) {
      console.error('Error copying activity:', error);
      const apiMessage = error.response?.data?.error;
      alert(apiMessage || 'Fehler beim Kopieren der Aktivität');
    }
  };

  // Die Liste enthält nur Anzeigefelder – für das Formular den vollen Datensatz holen
  const handleEdit = async (activity) => {
    try {
      const response = await activitiesAPI.getById(activity.id);
      setEditingActivity(response.data);
    } catch (error) {
      console.error('Error loading activity:', error);
      alert('Fehler beim Laden der Aktivität');
    }
  };

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEditSuccess = () => {
    setEditingActivity(null);
    loadActivities();
  };

  const handleEditCancel = () => {
    setEditingActivity(null);
  };

  if (!isModerator) {
    return <Navigate to="/" />;
  }

  if (authLoading || initialLoading) {
    return (
      <div className="moderation-page">
        <h1>Moderation - Aktivitäten verwalten</h1>
        <p>Lädt...</p>
      </div>
    );
  }

  // Filterung, Sortierung und Paginierung passieren serverseitig
  const paginatedActivities = activities;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="moderation-page">
      <h1>Moderation - Aktivitäten verwalten</h1>

      {editingActivity && (
        <div className="edit-activity-panel" ref={editPanelRef}>
          <div className="edit-activity-header">
            <h2>Aktivität bearbeiten</h2>
            <Button
              variant="outline"
              className="btn-sm"
              onClick={handleEditCancel}
              aria-label="Bearbeitung schließen"
              style={{ borderRadius: '9999px', padding: 'var(--spacing-2)' }}
            >
              <X size={18} />
            </Button>
          </div>
          <ActivityForm
            mode="edit"
            activityId={editingActivity.id}
            initialData={editingActivity}
            onSuccess={handleEditSuccess}
            onCancel={handleEditCancel}
          />
        </div>
      )}
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div className="filter-buttons">
          <button 
            onClick={() => handleFilterChange('all')}
            style={{
              padding: '8px 16px',
              marginRight: '10px',
              backgroundColor: filter === 'all' ? 'var(--color-primary)' : 'var(--color-secondary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            Alle ({counts.all})
          </button>
          <button
            onClick={() => handleFilterChange('pending')}
            style={{ 
              padding: '8px 16px', 
              marginRight: '10px',
              backgroundColor: filter === 'pending' ? '#ffc107' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            Wartend ({counts.pending})
          </button>
          <button
            onClick={() => handleFilterChange('active')}
            style={{ 
              padding: '8px 16px', 
              marginRight: '10px',
              backgroundColor: filter === 'active' ? '#28a745' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            Aktiv ({counts.active})
          </button>
          <button
            onClick={() => handleFilterChange('archived')}
            style={{ 
              padding: '8px 16px',
              backgroundColor: filter === 'archived' ? '#dc3545' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            Archiviert ({counts.archived})
          </button>
        </div>

        <div style={{ 
          position: 'relative',
          minWidth: '250px'
        }}>
          <input
            type="text"
            placeholder="Aktivitäten durchsuchen..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{
              padding: '8px 35px 8px 12px',
              border: '1px solid #ccc',
              borderRadius: 'var(--radius-sm)',
              width: '100%',
              fontSize: '14px'
            }}
          />
          <Search 
            size={18} 
            style={{ 
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d',
              pointerEvents: 'none'
            }}
          />
        </div>
      </div>
      
      {loading ? (
        <p>Lädt...</p>
      ) : total === 0 ? (
        <p>
          {searchTerm.trim()
            ? `Keine Aktivitäten gefunden, die "${searchTerm}" enthalten${filter !== 'all' ? ` (Status: ${filter})` : ''}.`
            : `Keine Aktivitäten mit Status "${filter}" gefunden.`
          }
        </p>
      ) : (
        <>
          {/* Desktop Table View */}
          <table className="activities-table activities-table-desktop">
          <thead>
            <tr>
              <th>Name</th>
              <th>Adresse</th>
              <th>Alter</th>
              <th>Website</th>
              <th>Status</th>
              <th>Erstellt am</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {paginatedActivities.map(activity => (
              <tr key={activity.id}>
                <td>{activity.name}</td>
                <td>{activity.address}</td>
                <td>{activity.age_min} - {activity.age_max} Jahre</td>
                <td>
                  {activity.website ? (
                    <a href={activity.website} target="_blank" rel="noopener noreferrer">
                      Link
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <span className={`status-badge status-${activity.status}`}>
                    {activity.status}
                  </span>
                </td>
                <td>{new Date(activity.created_at).toLocaleDateString('de-DE')}</td>
                <td className="action-buttons">
                  <Button
                    variant="outline"
                    className="btn-sm btn-approve"
                    onClick={() => handleApprove(activity.id)}
                    aria-label="Genehmigen"
                    title="Genehmigen"
                  >
                    <Check size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-sm btn-reject"
                    onClick={() => handleReject(activity.id)}
                    aria-label="Ablehnen"
                    title="Ablehnen"
                  >
                    <X size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-sm btn-delete"
                    onClick={() => handleDelete(activity.id)}
                    aria-label="Löschen"
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-sm btn-edit"
                    onClick={() => handleEdit(activity)}
                    aria-label="Bearbeiten"
                    title="Bearbeiten"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-sm btn-copy"
                    onClick={() => handleCopy(activity)}
                    aria-label="Kopieren"
                    title="Kopieren"
                  >
                    <Copy size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {/* Mobile Card View */}
          <div className="activities-cards-mobile space-y-3">
            {paginatedActivities.map(activity => (
              <Card key={activity.id} className="p-3" style={{ minHeight: 'auto' }}>
                <div className="space-y-2">
                  {/* Name, Adresse, Alter - horizontal */}
                  <div className="flex gap-2 text-xs" style={{ flexWrap: 'wrap' }}>
                    <span className="font-medium">{activity.name}</span>
                    <span>•</span>
                    <span className="text-gray-600">{activity.address}</span>
                    <span>•</span>
                    <span className="text-gray-600">{activity.age_min}-{activity.age_max} J.</span>
                  </div>
                  
                  {/* Link, Status, Erstellt am - horizontal */}
                  <div className="flex gap-2 items-center text-xs pt-2 border-t" style={{ flexWrap: 'wrap' }}>
                    {activity.website ? (
                      <a href={activity.website} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        Website
                      </a>
                    ) : (
                      <span className="text-gray-500">Kein Link</span>
                    )}
                    <span>•</span>
                    <span className={`status-badge status-${activity.status}`} style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                      {activity.status}
                    </span>
                    <span>•</span>
                    <span className="text-gray-600">{new Date(activity.created_at).toLocaleDateString('de-DE')}</span>
                  </div>

                  {/* Aktionen */}
                  <div className="flex gap-2 pt-2 border-t justify-end">
                    <Button
                      variant="outline"
                      onClick={() => handleApprove(activity.id)}
                      className="btn-sm btn-approve"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Genehmigen"
                    >
                      <Check size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleReject(activity.id)}
                      className="btn-sm btn-reject"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Ablehnen"
                    >
                      <X size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleEdit(activity)}
                      className="btn-sm btn-edit"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Bearbeiten"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(activity)}
                      className="btn-sm btn-copy"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Kopieren"
                    >
                      <Copy size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(activity.id)}
                      className="btn-sm btn-delete-mobile"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Löschen"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              padding: '15px 0'
            }}>
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outline"
                style={{
                  padding: '8px 16px',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Zurück
              </Button>
              
              <span style={{ fontSize: '14px', color: '#6c757d' }}>
                Seite {currentPage} von {totalPages} ({total} Einträge)
              </span>
              
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                style={{
                  padding: '8px 16px',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Weiter
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Moderation;
